/**
 * ContentQuickstartCard — state-aware dashboard card that guides users
 * through the masterplan → content plan → scheduling quickstart flow.
 *
 * Three states:
 *   1. No masterplan → CTA to /content?openMasterplan=true
 *   2. Masterplan exists, no plan → opens ContentPlanWizard modal
 *   3. Plan approved → shows next 3 publications + link to /scheduling
 *
 * Dismissable via localStorage['aime_quickstart_card_dismissed'] = 'true'.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, BookOpen, Calendar, ChevronRight, Loader2 } from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import ContentPlanWizard from '../SEO/ContentPlanWizard';
import { getQuickstartState, QuickstartState } from '../../services/api';
import './ContentQuickstartCard.css';

const DISMISSED_KEY = 'aime_quickstart_card_dismissed';

interface ContentQuickstartCardProps {
  projectId: string;
}

function formatPubDate(iso: string): string {
  try {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dt);
  } catch {
    return iso;
  }
}

const ContentQuickstartCard: React.FC<ContentQuickstartCardProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === 'true');
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<QuickstartState | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const fetchState = useCallback(async () => {
    if (!projectId) return;
    try {
      const s = await getQuickstartState(projectId);
      setState(s);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    setState(null);
    fetchState();
  }, [projectId, fetchState]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  // Don't render if dismissed
  if (dismissed) return null;

  // Don't render if no project
  if (!projectId) return null;

  if (loading) {
    return (
      <GlassCard className="cqc-card">
        <div className="cqc-loading">
          <Loader2 size={20} className="cqc-spin" />
          <span>Loading quickstart…</span>
        </div>
      </GlassCard>
    );
  }

  // State couldn't be fetched or missing project
  if (!state) return null;

  // User explicitly skipped both steps — hide card
  if (state.masterplan_skipped && state.plan_skipped) return null;
  if (state.masterplan_skipped && !state.has_masterplan) return null;
  if (state.plan_skipped && state.has_masterplan && !state.has_approved_plan) return null;

  // Determine which state to show
  const showState1 = !state.has_masterplan && !state.masterplan_skipped;
  const showState2 = state.has_masterplan && !state.has_approved_plan && !state.plan_skipped;
  const showState3 = state.has_approved_plan;

  if (!showState1 && !showState2 && !showState3) return null;

  return (
    <>
      <GlassCard className="cqc-card">
        <button className="cqc-dismiss" onClick={handleDismiss} aria-label="Dismiss">
          <X size={14} />
        </button>

        {showState1 && (
          <div className="cqc-state">
            <div className="cqc-icon-wrap">
              <BookOpen size={20} />
            </div>
            <div className="cqc-body">
              <h3 className="cqc-title">Build your content masterplan</h3>
              <p className="cqc-desc">Generate your topic strategy — we'll do it in ~30 seconds.</p>
              <GlassButton
                variant="primary"
                onClick={() => navigate('/content?openMasterplan=true')}
                className="cqc-cta"
              >
                Get started <ChevronRight size={14} />
              </GlassButton>
            </div>
          </div>
        )}

        {showState2 && (
          <div className="cqc-state">
            <div className="cqc-icon-wrap">
              <Calendar size={20} />
            </div>
            <div className="cqc-body">
              <h3 className="cqc-title">Generate your first 30-day plan</h3>
              <p className="cqc-desc">
                Your masterplan has {state.masterplan_streams.length} streams. Click to build a dated calendar.
              </p>
              <GlassButton
                variant="primary"
                onClick={() => setWizardOpen(true)}
                className="cqc-cta"
              >
                Open wizard <ChevronRight size={14} />
              </GlassButton>
            </div>
          </div>
        )}

        {showState3 && (
          <div className="cqc-state">
            <div className="cqc-icon-wrap">
              <Calendar size={20} />
            </div>
            <div className="cqc-body">
              <h3 className="cqc-title">View this week's articles</h3>
              {state.next_publications.length > 0 ? (
                <ul className="cqc-pubs">
                  {state.next_publications.map((pub) => (
                    <li key={pub.task_id} className="cqc-pub-row">
                      <span className="cqc-pub-title">{pub.title}</span>
                      <span className="cqc-pub-date">{formatPubDate(pub.scheduled_for)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cqc-desc">Your content plan is up to date.</p>
              )}
              <GlassButton
                variant="primary"
                onClick={() => navigate(`/scheduling/${projectId}`)}
                className="cqc-cta"
              >
                Open scheduling <ChevronRight size={14} />
              </GlassButton>
            </div>
          </div>
        )}
      </GlassCard>

      {/* State 2: ContentPlanWizard in modal mode */}
      {showState2 && (
        <ContentPlanWizard
          isOpen={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
            fetchState(); // re-query to update card state
          }}
          projectId={projectId}
        />
      )}
    </>
  );
};

export default ContentQuickstartCard;
